import { createStyles, makeStyles } from "@material-ui/styles";
import {
    CopyIcon,
    EditIcon,
    Input,
    ISearchFacetConfig,
    Link,
    LocationOption,
    LocationSearch,
    LocationSearchScopeEnum,
    mediator,
    MessageComposerIconButton,
    Page,
    PageActionButton,
    PageHeaderActions,
    PageSection,
    Paginator,
    RelodeTheme,
    SearchContainer,
    TableCellRoute,
    TableColumn,
    useRouter,
} from "@relode/core";
import { JobCandidateStatus } from "@relode/core/domain";
import qs from "qs";
import * as React from "react";

import { jobs } from "../../../shared/backend/controllers";
import { useJobSearch } from "../_infra/search/jobSearchContext";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            keywordInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            searchButton: {
                padding: 0,
            },
            searchButtonIcon: {
                fontSize: "1rem",
            },
            searchActions: {
                display: "flex",
            },
        }),
    { name: "JobSearchDesktop" }
);

interface InternalSearchState {
    searchText?: string;
    distanceRadius?: number;
    selectedLocationId?: number;
    selectedLocationName?: string;
}

export const JobSearchDesktop = () => {
    const classes = useStyles({});
    const [searchState, searchActions] = useJobSearch();
    const { history, location } = useRouter();

    const [
        internalSearchState,
        setInternalSearchState,
    ] = React.useState<InternalSearchState>({});
    const [backUrl, setBackUrl] = React.useState<string>(undefined);

    const { searchResults, searchCriteria } = searchState;
    const { canCopy } = searchResults;

    React.useEffect(() => {
        if (!!location.search) {
            setBackUrl(`/jobs${location.search}`);
        } else {
            setBackUrl("/jobs");
        }
    }, [location.search]);

    React.useEffect(() => {
        const initInternalSearchState: InternalSearchState = {
            searchText: searchCriteria.searchText || "",
            distanceRadius:
                searchCriteria.searchRadiusInMeters >= 0
                    ? searchCriteria.searchRadiusInMeters
                    : relodeConstants.DEFAULT_SEARCH_RADIUS_METERS,
            selectedLocationId: searchCriteria.locationId,
            selectedLocationName: searchCriteria.locationName,
        };
        setInternalSearchState(initInternalSearchState);
    }, [searchCriteria]);

    const handleSearch = () => {
        searchActions.searchBySelectedFilters(
            internalSearchState.searchText,
            internalSearchState.distanceRadius,
            internalSearchState.selectedLocationId,
            internalSearchState.selectedLocationName
        );
    };

    const handleLocationFilter = (option: LocationOption) => {
        setInternalSearchState({
            ...internalSearchState,
            selectedLocationId: option?.locationId,
            selectedLocationName: option?.locationName,
        });
    };

    const handleDistanceFilter = (miles: number) => {
        setInternalSearchState({
            ...internalSearchState,
            distanceRadius: miles,
        });
    };

    const handleMultipleSelectionFilter = (
        fId: string,
        fName: string,
        itemName?: string
    ) => (catItem?: { label: string; value: string }) => {
        searchActions.filterMultiple(fId, fName, itemName, catItem);
    };

    const handleFilter = (fId: string, fName: string) => (catItem?: {
        label: string;
        value: string;
    }) => {
        searchActions.filterSingle(fId, fName, catItem);
    };

    const handleCopyJob = (jobId: number) => {
        mediator.send("showAddJob", { jobId: jobId, isEditJob: false });
    };

    const handleEditJob = (jobId: number) => {
        mediator.send("showAddJob", {
            jobId: jobId,
            isEditJob: true,
            onComplete: handleJobSearchRefresh,
        });
    };

    const goToCandidatesSearch = (
        job: jobs.JobSearchJobResponse,
        jobCandidateStatus: JobCandidateStatus
    ) => {
        const criteria = {
            jobTitleName: job.jobName.trim(),
            jobId: job.jobId,
            jobCandidateStatusIds: [jobCandidateStatus.id],
            jobCandidateStatusNames: [jobCandidateStatus.description],
            pageIndex: 1,
            pageSize: 25,
        };
        history.push(
            `/candidates${qs.stringify(criteria, {
                skipNulls: true,
                addQueryPrefix: true,
                sort: (a, b) => a.localeCompare(b),
            })}`
        );
    };

    const facetConfig: ISearchFacetConfig = {
        handleMultipleSelection: handleMultipleSelectionFilter,
        handleSingleSelection: handleFilter,
        facets: [
            {
                id: "jobStatus",
                title: "job status",
                idPropName: "jobStatusIds",
                namePropName: "jobStatusNames",
                isMultiple: true,
            },
            {
                id: "location",
                title: "job location",
                idPropName: "locationId",
                namePropName: "locationName",
                isMultiple: false,
            },
            {
                id: "jobPriorityType",
                title: "job grade",
                idPropName: "jobPriorityTypeId",
                namePropName: "jobPriorityTypeName",
                isMultiple: false,
            },
            {
                id: "jobType",
                title: "job type",
                idPropName: "jobTypeIds",
                namePropName: "jobTypeNames",
                isMultiple: true,
            },
            {
                id: "team",
                title: "organization",
                idPropName: "teamId",
                namePropName: "teamName",
                isMultiple: false,
            },
            {
                id: "jobScope",
                title: "job scope",
                idPropName: "jobScope",
                namePropName: "jobScopeName",
                isMultiple: false,
            },
        ],
    };

    const getColumns = (): TableColumn[] => [
        {
            title: "job title",
            field: "jobName",
            sortable: true,
            columnSize: "large",
            truncateText: true,
            render: (rowData: jobs.JobSearchJobResponse) => (
                <TableCellRoute
                    title={`${rowData.jobName || "n/a"}`}
                    pathname={`/jobs/${rowData.jobId}`}
                    backUrl={backUrl}
                >
                    {rowData.jobName || "n/a"}
                </TableCellRoute>
            ),
        },
        {
            title: "location",
            field: "locationName",
            sortable: true,
            columnSize: "medium",
            truncateText: true,
        },
        {
            title: "job status",
            field: "jobStatusName",
            sortable: true,
            columnSize: "small",
        },
        {
            title: "days open",
            field: "daysOpen",
            sortable: true,
            align: "center",
            columnSize: "small",
            emptyValuePlaceholder: "--",
        },
        {
            title: "submitted",
            field: "submittedCandidateCount",
            sortable: true,
            align: "center",
            columnSize: "smaller",
            render: (rowData: jobs.JobSearchJobResponse) =>
                rowData.submittedCandidateCount > 0 ? (
                    <Link
                        onClick={() =>
                            goToCandidatesSearch(
                                rowData,
                                JobCandidateStatus.Submitted
                            )
                        }
                    >
                        {rowData.submittedCandidateCount}
                    </Link>
                ) : (
                    rowData.submittedCandidateCount
                ),
        },
        {
            title: "interviewing",
            field: "interviewingCandidateCount",
            sortable: true,
            align: "center",
            columnSize: "smaller",
            render: (rowData: jobs.JobSearchJobResponse) =>
                rowData.interviewingCandidateCount > 0 ? (
                    <Link
                        onClick={() =>
                            goToCandidatesSearch(
                                rowData,
                                JobCandidateStatus.Interviewing
                            )
                        }
                    >
                        {rowData.interviewingCandidateCount}
                    </Link>
                ) : (
                    rowData.interviewingCandidateCount
                ),
        },
        {
            title: "offered",
            field: "offeredCandidateCount",
            sortable: true,
            align: "center",
            columnSize: "smaller",
            render: (rowData: jobs.JobSearchJobResponse) =>
                rowData.offeredCandidateCount > 0 ? (
                    <Link
                        onClick={() =>
                            goToCandidatesSearch(
                                rowData,
                                JobCandidateStatus.Offered
                            )
                        }
                    >
                        {rowData.offeredCandidateCount}
                    </Link>
                ) : (
                    rowData.offeredCandidateCount
                ),
        },
        {
            title: "hired",
            field: "hiredCandidateCount",
            sortable: true,
            align: "center",
            columnSize: "smaller",
            render: (rowData: jobs.JobSearchJobResponse) =>
                rowData.hiredCandidateCount > 0 ? (
                    <Link
                        onClick={() =>
                            goToCandidatesSearch(
                                rowData,
                                JobCandidateStatus.Hired
                            )
                        }
                    >
                        {rowData.hiredCandidateCount}
                    </Link>
                ) : (
                    rowData.hiredCandidateCount
                ),
        },
        {
            title: "positions",
            field: "openPositionCount",
            sortable: true,
            align: "center",
            columnSize: "smallest",
        },
        {
            title: "actions",
            field: "actions",
            align: "right",
            sortable: false,
            render: (rowData: jobs.JobSearchJobResponse) => (
                <div className={classes.searchActions}>
                    <MessageComposerIconButton
                        id="messageJob"
                        jobId={rowData.jobId}
                        jobName={rowData.jobName}
                    />
                    {canCopy ? (
                        <PageActionButton
                            onClick={() => handleCopyJob(rowData.jobId)}
                            id="copyJob"
                            color="primary"
                            title="copy job"
                        >
                            <CopyIcon />
                        </PageActionButton>
                    ) : null}
                    {rowData.canEdit ? (
                        <PageActionButton
                            onClick={() => handleEditJob(rowData.jobId)}
                            id="editJob"
                            color="primary"
                            title="edit job"
                        >
                            <EditIcon />
                        </PageActionButton>
                    ) : null}
                </div>
            ),
        },
    ];

    const handleTextSearchChange = (event: React.ChangeEvent<any>) => {
        setInternalSearchState({
            ...internalSearchState,
            searchText: event.target.value,
        });
    };

    const handleTextSearchKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            searchActions.searchText(internalSearchState.searchText);
        }
    };

    const handleRequestSort = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        property: string
    ) => {
        searchActions.sort(property);
    };

    const handleJobSearchRefresh = async () => {
        await searchActions.search(searchCriteria);
    };

    const renderActionBar = (): React.ReactNode => {
        if (!searchResults) return;
        const { totalResults } = searchResults;
        const { pageSize, pageIndex } = searchCriteria;
        const start = (pageIndex - 1) * pageSize + 1;
        const remaining =
            totalResults < pageSize ? totalResults : totalResults - start;
        const maxOnPage =
            remaining < pageSize ? totalResults : pageIndex * pageSize;

        return (
            <PageHeaderActions
                right={
                    !!searchResults &&
                    totalResults > 0 && (
                        <Paginator
                            baseId="jobsSearch"
                            isBusy={searchState.searching}
                            total={totalResults}
                            range={{
                                start,
                                end: maxOnPage,
                            }}
                            showAsRange
                            enableTooManyResultsMessage
                            onNavigate={changePage}
                        />
                    )
                }
            />
        );
    };

    const changePage = (direction: "prev" | "next") => {
        searchActions.navigate(direction);
    };

    return (
        <>
            <Page
                data-cy="jobsSearch-page"
                id="jobsSearchPage"
                label="jobs"
                isBusy={searchState.searching}
                actions={renderActionBar()}
                busyType="bar"
            >
                <PageSection>
                    <SearchContainer
                        id="jobsSearch"
                        entityName="jobs"
                        facets={searchResults?.facets}
                        facetConfig={facetConfig}
                        hasResults={searchResults?.totalResults > 0}
                        criteria={searchCriteria}
                        onRequestSort={handleRequestSort}
                        getColumns={getColumns}
                        sortDir={searchCriteria.sortDir}
                        sortField={searchCriteria.sortField}
                        totalCount={searchResults?.totalResults}
                        data={() => searchResults?.jobs}
                        onSearch={handleSearch}
                        searchDisabled={
                            internalSearchState.searchText ===
                                searchCriteria.searchText &&
                            internalSearchState.distanceRadius ===
                                searchCriteria.searchRadiusInMeters &&
                            internalSearchState.selectedLocationId ===
                                searchCriteria.locationId
                        }
                    >
                        <div>
                            <Input
                                autoComplete="off"
                                fullWidth
                                className={classes.keywordInput}
                                value={internalSearchState.searchText}
                                autoFocus={true}
                                label="name, position, etc"
                                onChange={handleTextSearchChange}
                                onKeyPress={handleTextSearchKeyPress}
                                inputProps={{
                                    id: "jobsSearchKeywordInput",
                                    maxLength: 150,
                                }}
                                hideCharacterLimit={true}
                            />
                            <LocationSearch
                                id="jobsSearch"
                                label="city, state or zip"
                                selectedLocation={
                                    !!internalSearchState.selectedLocationId
                                        ? {
                                              locationId:
                                                  internalSearchState.selectedLocationId,
                                              locationName:
                                                  internalSearchState.selectedLocationName,
                                          }
                                        : null
                                }
                                onSelect={handleLocationFilter}
                                onDistanceChange={handleDistanceFilter}
                                searchRadiusInMeters={
                                    internalSearchState.distanceRadius
                                }
                                scope={LocationSearchScopeEnum.ExcludeAny}
                            />
                        </div>
                    </SearchContainer>
                </PageSection>
            </Page>
        </>
    );
};
